import { useEffect, useState } from "react";
import { z } from "zod";

/**
 * Browser-local store for "creations" — outputs an anonymous visitor saves
 * from the free /tools/* builders. Mirrors the zod + localStorage pattern
 * in pendingSeed.ts: a single storage key holds a JSON array, every read is
 * defensive (corrupt or partially-invalid data degrades to a clean list),
 * and writes broadcast a CustomEvent so any open view can refresh.
 *
 * Persistence is intentionally browser-only for now — no backend, no API.
 * Because it lives in the same localStorage the visitor used while anonymous,
 * their saved work simply reappears in /projects once they log in.
 */

export const CREATIONS_STORAGE_KEY = "shotprompter:creations";
export const CREATIONS_CHANGED_EVENT = "shotprompter:creations-changed";

/**
 * sessionStorage flag that prevents re-running the localStorage→server
 * migration on every navigation within the same browser tab.
 * - Set to "1" on a successful migration (or when localStorage is empty).
 * - Left unset on failure so the next mount retries.
 * - Lives in sessionStorage (not localStorage) so a new tab or hard
 *   refresh always re-checks, while same-tab soft navigations skip it.
 */
export const MIGRATION_SESSION_FLAG = "shotprompter:creations:migrated-this-session";

export const CREATION_TYPES = [
  "shot-list",
  "shooting-schedule",
  "screenplay-format",
  "shot-prompt",
  "character",
  "location",
  "prop",
  "costume",
  "screenplay",
  "logline",
  "character-bio",
  "video-prompt",
] as const;

export type CreationType = (typeof CREATION_TYPES)[number];

const CreationSchema = z.object({
  id: z.string().min(1),
  type: z.enum(CREATION_TYPES),
  title: z.string(),
  toolPath: z.string(),
  content: z.string(),
  createdAt: z.string(),
});

export type Creation = z.infer<typeof CreationSchema>;

function readAll(): Creation[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(CREATIONS_STORAGE_KEY);
  } catch {
    return [];
  }
  if (raw == null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  // Drop entries that fail validation rather than discarding the whole list.
  const valid: Creation[] = [];
  for (const entry of parsed) {
    const result = CreationSchema.safeParse(entry);
    if (result.success) valid.push(result.data);
  }
  return valid;
}

function writeAll(creations: Creation[]): void {
  try {
    localStorage.setItem(CREATIONS_STORAGE_KEY, JSON.stringify(creations));
  } catch {
    // Storage disabled / over quota — best-effort, matches useLocalDraft.
  }
}

function dispatchChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(CREATIONS_CHANGED_EVENT));
  } catch {
    // SSR / no window — nothing to notify.
  }
}

function newId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the Math.random fallback below.
  }
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** All saved creations, newest first. Corrupt/missing storage yields []. */
export function listCreations(): Creation[] {
  const all = readAll();
  return [...all].sort((a, b) => {
    const at = Date.parse(a.createdAt);
    const bt = Date.parse(b.createdAt);
    const av = Number.isNaN(at) ? 0 : at;
    const bv = Number.isNaN(bt) ? 0 : bt;
    return bv - av;
  });
}

/**
 * Persist a new creation. Generates the id + createdAt, prepends it to the
 * stored list, broadcasts a change event, and returns the saved record.
 */
export function saveCreation(
  input: Omit<Creation, "id" | "createdAt">,
): Creation {
  const creation: Creation = {
    ...input,
    id: newId(),
    createdAt: new Date().toISOString(),
  };
  const next = [creation, ...readAll()];
  writeAll(next);
  dispatchChanged();
  return creation;
}

/** Remove a creation by id, persist, and broadcast a change event. */
export function deleteCreation(id: string): void {
  const next = readAll().filter((c) => c.id !== id);
  writeAll(next);
  dispatchChanged();
}

// ---------------------------------------------------------------------------
// Migration helper
// ---------------------------------------------------------------------------

/**
 * Shape of one item sent to the backend migration endpoint.
 * Mirrors CreationMigrationItem in src/api/schemas/creations.py.
 */
export type CreationMigrationItem = {
  client_id: string;
  type: CreationType;
  title: string;
  tool_path: string;
  content: string;
  created_at: string;
};

/**
 * migrateAndClearLocal — drain the localStorage creations key to the server.
 *
 * Accepts a `send` callback (injected by the hook) so this function stays
 * free of any network knowledge. The hook passes `migrateCreations` from
 * api/creations.ts.
 *
 * - If the key is empty, returns early without calling `send`.
 * - On success from `send`, clears the localStorage key.
 * - On error from `send`, re-throws without touching localStorage.
 */
export async function migrateAndClearLocal(
  send: (items: CreationMigrationItem[]) => Promise<{ migrated: number; skipped: number }>,
): Promise<{ migrated: number; skipped: number; items: CreationMigrationItem[] }> {
  const rows = readAll();

  if (rows.length === 0) {
    return { migrated: 0, skipped: 0, items: [] };
  }

  const items: CreationMigrationItem[] = rows.map((c) => ({
    client_id: c.id,
    type: c.type,
    title: c.title,
    tool_path: c.toolPath,
    content: c.content,
    created_at: c.createdAt,
  }));

  // Throws on error — localStorage is left intact.
  const result = await send(items);

  // Only clear after a confirmed success.
  try {
    localStorage.removeItem(CREATIONS_STORAGE_KEY);
  } catch {
    // Storage disabled — best-effort.
  }

  return { ...result, items };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * @deprecated Use the server-aware `useCreations` from `@/api/hooks/useCreations` instead.
 * This localStorage-only hook is kept for tests and the anon-only call path.
 *
 * React hook returning the current creations list. Re-reads on the in-tab
 * change event (same-document saves/deletes) and on the native `storage`
 * event (changes made in another tab).
 */
export function useCreations(): Creation[] {
  const [creations, setCreations] = useState<Creation[]>(() => listCreations());

  useEffect(() => {
    const refresh = () => setCreations(listCreations());
    refresh();
    window.addEventListener(CREATIONS_CHANGED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CREATIONS_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return creations;
}
